import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import DocumentWallet from '../components/DocumentWallet';
import AuditLog from '../components/AuditLog';
import EntityLink from '../components/EntityLink';
import VoidArInvoiceButton from '../components/VoidArInvoiceButton';
import { useAuth } from '../context/AuthContext';
import ConsultingLines from '../components/ConsultingLines';

const INVOICE_TYPE_LABELS = { standard: 'Standard', passthrough: 'Allowance / Reimbursement', fte_referral: 'FTE Referral', credit_note: 'Credit Note', consulting: 'Business Consulting' };
const PAYMENT_METHOD_LABELS = { ach: 'ACH', check: 'Check', wire: 'Wire' };

function fmt(n) {
  return '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function Field({ label, value, link }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{label}</div>
      <div>{link && value ? <EntityLink type={link.type} id={link.id}>{value}</EntityLink> : (value || '—')}</div>
    </div>
  );
}

export default function ARInvoiceProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  function load() {
    setError(false);
    api.get(`/ar-invoices/${id}`)
      .then(r => setData(r.data))
      .catch(() => setError(true));
  }
  useEffect(() => { load(); }, [id]);

  if (error) {
    return <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />;
  }
  if (!data) return <StateBlock type="loading" />;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Back</button>
          <h1 className="page-title">{data.invoice_number}</h1>
          <Badge>{data.status}</Badge>
          <Badge variant={data.invoice_type === 'passthrough' ? 'info' : data.invoice_type === 'consulting' ? 'success' : undefined}>
            {INVOICE_TYPE_LABELS[data.invoice_type] || INVOICE_TYPE_LABELS.standard}
          </Badge>
        </div>
        {user?.role === 'admin' && (
          <div className="page-actions">
            <VoidArInvoiceButton invoice={{ ...data, id: Number(id) }} onVoided={load} className="btn btn-secondary" />
          </div>
        )}
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Invoice Details</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Field label="Client" value={data.client_name} link={data.client_id ? { type: 'client', id: data.client_id } : null} />
          <Field label="Contractor" value={data.contractor_name} link={data.contractor_id ? { type: 'contractor', id: data.contractor_id } : null} />
          <Field label="Work Order #" value={data.client_wo_number} link={data.wo_id ? { type: 'work_order', id: data.wo_id } : null} />
          <Field label="PO #" value={data.po_number} link={data.po_id ? { type: 'purchase_order', id: data.po_id } : null} />
          <Field label="Engagement Type" value={(data.engagement_type || '').replace(/_/g, ' ')} />
          <Field label="Invoice Date" value={data.invoice_date} />
          <Field label="Period" value={data.period_start && data.period_end ? `${data.period_start} – ${data.period_end}` : null} />
          {data.invoice_type !== 'consulting' && <>
            <Field label="Hours Billed" value={data.hours_billed} />
            <Field label="Hours Approved" value={data.hours} />
            <Field label="Rate" value={data.rate ? `$${data.rate}/hr` : null} />
          </>}
          <Field label="Due Date" value={data.due_date} />
        </div>
        {data.invoice_type === 'consulting' && <div style={{ marginTop: 16 }}><ConsultingLines lines={data.lines || []} invoice={data} /></div>}
        {data.notes && (
          <div style={{ marginTop: 16 }}>
            <Field label="Notes" value={data.notes} />
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 20 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Invoice Amount</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.amount)}</div>
          </div>
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Outstanding</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(data.summary.outstanding)}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Payments ({(data.payments || []).length})</h3>
        {(!data.payments || data.payments.length === 0) ? (
          <StateBlock type="empty" title="No payments recorded yet" />
        ) : (
          <table className="data-table">
            <thead><tr><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th>Method</th><th>Reference</th><th>Status</th></tr></thead>
            <tbody>
              {data.payments.map(p => (
                <tr key={p.id}>
                  <td>{p.payment_date}</td>
                  <td style={{ textAlign: 'right' }}>{fmt(p.cash_amount)}</td>
                  <td>{PAYMENT_METHOD_LABELS[p.payment_method] || '—'}</td>
                  <td>{p.reference || '—'}</td>
                  <td><Badge variant={p.verification_status === 'verified' ? 'success' : 'warning'}>{p.verification_status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="form-hint" style={{ marginTop: 12 }}>
          To edit this invoice or record a payment, use the AR Invoices list.
        </div>
      </div>

      <div className="card" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
        <DocumentWallet entityType="ar_invoice" entityId={data.id} entityName={data.invoice_number} />
      
      <AuditLog tableName="ar_invoices" recordId={id} /></div>
    </div>
  );
}
